import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CalendarIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  AlertCircleIcon,
  CheckIcon,
} from "../../../../Icons";
import CollapsibleSection from "../CollapsibleSection";
import ScheduleVisitModal from "./ScheduleVisitModal";
import ViewFrequencyModal from "./ViewFrequencyModal";
import { PATIENT_SECTION_TONE } from "../../patientTheme";
import {
  getScheduledVisits,
  updateScheduledVisit,
  deleteScheduledVisit,
  createScheduledVisit,
  getPatientAssignments,
  getVisitFrequencies,
} from "../../../../services/patientService";
import {
  VISIT_TYPES,
  getVisitTypeColor,
  formatVisitTypeLabel,
  resolveEvalSectionFromVisit,
  FREQUENCY_MISSING_HEX,
  FREQUENCY_MISSING_LABEL,
  FREQUENCY_MISSING_SWATCH_SHADOW,
  visitChipBoxShadow,
} from "./scheduleConstants";
import { dailyLogChartNavState } from "../clinicalForms/dailyLogEval";
import {
  isVisitUnauthorized,
  visitAuthorizationTitle,
  UNAUTHORIZED_HEX,
  UNAUTHORIZED_LABEL,
} from "../../visitAuthorization";
import { isFrequencyMissingVisit } from "./visitFrequency";

const DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const getMonthDays = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month - 1, prevMonthDays - i), outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), outside: false });
  }
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      cells.push({ date: new Date(year, month + 1, d), outside: true });
    }
  }
  return cells;
};

const getWeekDays = (anchor) => {
  const d = startOfDay(anchor);
  const sunday = addDays(d, -d.getDay());
  return Array.from({ length: 7 }, (_, i) => ({
    date: addDays(sunday, i),
    outside: false,
  }));
};

const fmtDate = (d) => {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const PatientScheduleSection = ({
  patientId,
  patient = null,
  episodeMeta,
  canEdit = false,
  /** Removing a visit also removes its form — Admin/OA only. */
  canDelete = false,
  /** When false, calendar stays usable but Eval form navigation is blocked. */
  evalTabEnabled = true,
  /** Bump after authorizations save so visit coverage flags reload. */
  reloadKey = 0,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorDate, setAnchorDate] = useState(() => {
    if (episodeMeta?.startDate) return startOfDay(new Date(episodeMeta.startDate));
    return startOfDay(new Date());
  });
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingVisit, setEditingVisit] = useState(null);
  const [defaultDate, setDefaultDate] = useState("");
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [viewMode, setViewMode] = useState("month");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [frequencyOpen, setFrequencyOpen] = useState(false);
  const [frequencies, setFrequencies] = useState([]);

  const episodeStart = episodeMeta?.startDate ? new Date(episodeMeta.startDate) : null;
  const episodeEnd = episodeMeta?.endDate ? new Date(episodeMeta.endDate) : null;

  const loadVisits = useCallback(async () => {
    if (!patientId || !episodeMeta?.id) return;
    setLoading(true);
    try {
      const res = await getScheduledVisits(patientId, { episodeId: episodeMeta.id });
      const data = res?.result?.visits || res?.visits || [];
      setVisits(data);
    } catch (err) {
      console.error("Failed to load visits", err);
    } finally {
      setLoading(false);
    }
  }, [patientId, episodeMeta?.id]);

  const loadStaff = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await getPatientAssignments(patientId);
      const assigned =
        res?.result?.assignedStaff ||
        res?.assignedStaff ||
        res?.data?.assignedStaff ||
        [];
      setAssignedStaff(Array.isArray(assigned) ? assigned : []);
    } catch {
      setAssignedStaff([]);
    }
  }, [patientId]);

  const loadFrequencies = useCallback(async () => {
    if (!patientId || !episodeMeta?.id) return;
    try {
      const res = await getVisitFrequencies(patientId, {
        episodeId: episodeMeta.id,
      });
      setFrequencies(res?.result?.frequencies || res?.frequencies || []);
    } catch {
      setFrequencies([]);
    }
  }, [patientId, episodeMeta?.id]);

  useEffect(() => {
    loadVisits();
    loadStaff();
    loadFrequencies();
  }, [loadVisits, loadStaff, loadFrequencies, location.key, reloadKey]);

  useEffect(() => {
    if (episodeMeta?.startDate) {
      setAnchorDate(startOfDay(new Date(episodeMeta.startDate)));
    }
  }, [episodeMeta?.id, episodeMeta?.startDate]);

  const visitsByDate = useMemo(() => {
    const map = {};
    (visits || []).forEach((v) => {
      const key = v.visitDate?.slice(0, 10);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(v);
    });
    return map;
  }, [visits]);

  const cells = useMemo(() => {
    if (viewMode === "week") return getWeekDays(anchorDate);
    if (viewMode === "day") return [{ date: startOfDay(anchorDate), outside: false }];
    return getMonthDays(anchorDate.getFullYear(), anchorDate.getMonth());
  }, [anchorDate, viewMode]);

  const periodLabel = useMemo(() => {
    if (viewMode === "day") {
      return anchorDate.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    if (viewMode === "week") {
      const week = getWeekDays(anchorDate);
      const start = week[0].date;
      const end = week[6].date;
      const fmt = (d) =>
        d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      return `${fmt(start)} – ${fmt(end)}`;
    }
    return `${monthNames[anchorDate.getMonth()]} ${anchorDate.getFullYear()}`;
  }, [anchorDate, viewMode]);

  const goPrev = () => {
    setAnchorDate((d) => {
      if (viewMode === "day") return addDays(d, -1);
      if (viewMode === "week") return addDays(d, -7);
      return new Date(d.getFullYear(), d.getMonth() - 1, 1);
    });
  };

  const goNext = () => {
    setAnchorDate((d) => {
      if (viewMode === "day") return addDays(d, 1);
      if (viewMode === "week") return addDays(d, 7);
      return new Date(d.getFullYear(), d.getMonth() + 1, 1);
    });
  };

  /**
   * The modal no longer asks for a date, so a booking started from the toolbar
   * rather than a day cell lands on today — or on the episode start when today
   * falls outside the episode.
   */
  const fallbackVisitDate = () => {
    const today = startOfDay(new Date());
    if (episodeStart && today < startOfDay(episodeStart)) {
      return fmtDate(episodeStart);
    }
    if (episodeEnd && today > startOfDay(episodeEnd)) {
      return fmtDate(episodeEnd);
    }
    return fmtDate(today);
  };

  const openAdd = (date) => {
    setModalMode("add");
    setEditingVisit(null);
    setDefaultDate(date || fallbackVisitDate());
    setModalOpen(true);
  };

  const openEdit = (visit) => {
    setModalMode("edit");
    setEditingVisit(visit);
    setDefaultDate("");
    setModalOpen(true);
  };

  const goToForm = (visit) => {
    if (!evalTabEnabled) {
      toast.error("Eval & Orders is not enabled for this agency");
      return;
    }
    if (visit?.dailyLogId) {
      navigate(`/general-forms/daily-log/${visit.dailyLogId}`, {
        state: dailyLogChartNavState({
          patientId,
          returnTo: `${location.pathname}${location.search || ""}`,
          patient,
        }),
      });
      return;
    }
    if (!visit?.clinicalFormId) {
      toast.error("No form is assigned to this visit yet");
      return;
    }
    navigate(`/patients/${patientId}/forms/${visit.clinicalFormId}`, {
      state: {
        returnTo: `${location.pathname}${location.search || ""}`,
        tab: "eval",
        evalSection: resolveEvalSectionFromVisit(
          visit.discipline,
          visit.visitType,
        ),
      },
    });
  };

  const openEvalForVisit = (visit) => {
    if (canEdit) {
      openEdit(visit);
      return;
    }
    if (visit.clinicalFormId || visit.dailyLogId) {
      goToForm(visit);
      return;
    }
    toast.error("This visit has no form yet");
  };

  const handleSubmit = async (payload, { goToForm: shouldOpenForm } = {}) => {
    const isFilledEdit = modalMode === "edit" && editingVisit?.formFilled;
    if (!payload?.visitDate) {
      toast.error("Visit date is required");
      return;
    }
    if (!isFilledEdit) {
      if (!payload?.discipline) {
        toast.error("Position is required");
        return;
      }
      if (!payload?.staffId) {
        toast.error("Staff is required");
        return;
      }
      if (!payload?.formType) {
        toast.error("Visit type is required");
        return;
      }
    }
    setSaving(true);
    try {
      let visit = null;
      if (modalMode === "edit" && editingVisit) {
        const res = await updateScheduledVisit(
          patientId,
          editingVisit.id,
          payload,
        );
        visit = res?.result?.visit || res?.visit || null;
        toast.success("Visit updated");
      } else {
        const res = await createScheduledVisit(patientId, {
          ...payload,
          episodeId: episodeMeta?.id,
        });
        visit = res?.result?.visit || res?.visit || null;
        toast.success("Visit scheduled with an assigned form");
      }
      setModalOpen(false);
      loadVisits();
      loadFrequencies();
      if (shouldOpenForm && visit) {
        goToForm(visit);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to save visit");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVisit = async (visitId) => {
    setDeleting(true);
    try {
      await deleteScheduledVisit(patientId, visitId);
      toast.success("Visit deleted");
      setModalOpen(false);
      loadVisits();
      loadFrequencies();
    } catch (err) {
      toast.error(err?.message || "Failed to delete visit");
    } finally {
      setDeleting(false);
    }
  };

  const isInEpisode = (date) => {
    if (!episodeStart || !episodeEnd) return true;
    const d = startOfDay(date);
    const s = startOfDay(episodeStart);
    const e = startOfDay(episodeEnd);
    return d >= s && d <= e;
  };

  const today = fmtDate(new Date());

  const episodeLabel = episodeMeta
    ? `Episode ${episodeMeta.episodeNumber}: ${new Date(episodeMeta.startDate).toLocaleDateString(
        "en-US",
        { month: "2-digit", day: "2-digit", year: "2-digit" },
      )} - ${new Date(episodeMeta.endDate).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      })}`
    : "No Episode";

  const episodeSubtitle = episodeMeta
    ? `${new Date(episodeMeta.startDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })} – ${new Date(episodeMeta.endDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })} · 60-Day Episode`
    : null;

  const renderDayCell = (cell, idx, { tall = false, showWeekday = false } = {}) => {
    const dateStr = fmtDate(cell.date);
    const dayVisits = visitsByDate[dateStr] || [];
    const inEpisode = isInEpisode(cell.date);
    const visibleVisits = inEpisode
      ? [...dayVisits].sort((a, b) =>
          String(a.startTime || "").localeCompare(String(b.startTime || "")),
        )
      : [];
    const isToday = dateStr === today;
    const hasVisits = visibleVisits.length > 0;
    const accent = hasVisits
      ? getVisitTypeColor(visibleVisits[0].visitType, visibleVisits[0].color)
      : null;

    return (
      <div
        key={idx}
        className={`group relative flex flex-col border-b border-r border-indigo-100 p-1.5 transition-colors ${
          tall ? "min-h-[280px]" : "min-h-[90px]"
        } ${
          cell.outside
            ? "bg-slate-50/80"
            : inEpisode
              ? "bg-white hover:bg-indigo-50/40"
              : "bg-slate-50"
        }`}
      >
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <span
              className={`w-6 h-6 text-xs font-medium leading-tight rounded-full flex items-center justify-center ${
                isToday
                  ? "bg-indigo-600 text-white"
                  : cell.outside
                    ? "bg-transparent text-slate-300"
                    : !inEpisode
                      ? "bg-slate-100 text-slate-400"
                      : hasVisits
                        ? "text-white"
                        : "text-indigo-800 group-hover:text-indigo-700"
              }`}
              style={
                !isToday && !cell.outside && inEpisode && hasVisits
                  ? { backgroundColor: accent }
                  : undefined
              }
            >
              {cell.date.getDate()}
            </span>
            {showWeekday && (
              <span className="text-[11px] font-semibold text-indigo-600 uppercase">
                {DAYS[cell.date.getDay()]}
              </span>
            )}
          </div>
          {canEdit && inEpisode && !cell.outside && (
            <button
              type="button"
              onClick={() => openAdd(dateStr)}
              className="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center h-4 w-4 rounded bg-white border border-indigo-200 text-indigo-400 hover:text-white hover:bg-indigo-500 hover:border-indigo-500 transition-colors"
              style={hasVisits ? { borderColor: accent } : undefined}
              title="Add visit"
            >
              <PlusIcon size={10} />
            </button>
          )}
        </div>

        <div
          className={`space-y-0.5 ${
            !hasVisits ? "flex flex-col flex-1 justify-center min-h-[48px]" : ""
          }`}
        >
          {visibleVisits.map((v) => {
            const bgColor = getVisitTypeColor(v.visitType, v.color);
            const typeLabel = formatVisitTypeLabel(v.visitType);
            // Keep the visit-type colour and add a red ring plus a glyph, so
            // the chip still says what kind of visit it is while reading as a
            // problem at a glance.
            const unauthorized = isVisitUnauthorized(v.authorization);
            const authTitle = visitAuthorizationTitle(v.authorization);
            const filled = Boolean(v.formFilled);
            const frequencyMissing = isFrequencyMissingVisit(v);
            const staffRole = v.staffRole || v.discipline || "";
            const staffLong = v.staffName || "Unassigned";
            const staffShort = v.staffInitials || "Unassigned";
            const stateTitle = [
              filled
                ? `Filled · ${v.clinicalFormName || typeLabel}`
                : `Assigned, not filled · ${v.clinicalFormName || typeLabel}`,
              frequencyMissing ? FREQUENCY_MISSING_LABEL : "",
              authTitle,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div
                key={v.id}
                role="button"
                tabIndex={0}
                onClick={() => openEvalForVisit(v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openEvalForVisit(v);
                  }
                }}
                className={`group/event flex items-center gap-1 px-1 py-0.5 rounded text-[10px] leading-tight truncate cursor-pointer hover:brightness-110 ${
                  filled ? "text-white" : "text-slate-700"
                }`}
                style={{
                  backgroundColor: filled ? bgColor : `${bgColor}22`,
                  boxShadow: visitChipBoxShadow({
                    typeColor: bgColor,
                    filled,
                    frequencyMissing,
                    unauthorized,
                    unauthorizedHex: UNAUTHORIZED_HEX,
                  }),
                }}
                title={stateTitle}
              >
                {unauthorized ? (
                  <AlertCircleIcon
                    size={9}
                    className="shrink-0"
                    aria-label={UNAUTHORIZED_LABEL}
                  />
                ) : filled ? (
                  <CheckIcon size={9} className="shrink-0" />
                ) : null}
                {frequencyMissing ? (
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0 ring-1 ring-white"
                    style={{ backgroundColor: FREQUENCY_MISSING_HEX }}
                    aria-label={FREQUENCY_MISSING_LABEL}
                  />
                ) : null}
                <span
                  className={`font-semibold ${tall ? "min-w-0 truncate" : "shrink-0"}`}
                >
                  {tall ? v.clinicalFormName || typeLabel : typeLabel}
                </span>
                <span className="ml-auto min-w-0 truncate text-right font-medium opacity-95">
                  {tall ? staffLong : staffShort}
                  {staffRole ? ` · ${staffRole}` : ""}
                </span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(v);
                    }}
                    className="hidden group-hover/event:inline-flex items-center justify-center h-3.5 w-3.5 rounded-sm bg-white/25 hover:bg-white/45 shrink-0"
                    title="Edit visit"
                  >
                    <EditIcon size={8} />
                  </button>
                )}
              </div>
            );
          })}
          {!hasVisits && (
            <p
              className={`text-center leading-snug px-1 select-none ${
                tall ? "text-[12px]" : "text-[10px]"
              } ${
                !inEpisode || cell.outside
                  ? "text-slate-300 font-normal italic"
                  : "text-indigo-300 font-medium"
              }`}
            >
              {!inEpisode || cell.outside ? "Unavailable" : "No visits"}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <CollapsibleSection
        title="Schedule"
        icon={<CalendarIcon size={14} />}
        tone={PATIENT_SECTION_TONE}
        defaultOpen
        actions={
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setFrequencyOpen(true)}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 bg-white border border-indigo-200 rounded-md text-[11px] font-semibold text-indigo-800 hover:bg-indigo-50"
            >
              <CalendarIcon size={12} className="text-indigo-500" />
              View Frequency
            </button>
            <div
              className="inline-flex items-center gap-1.5 h-7 px-2.5 bg-white border border-indigo-200 rounded-md pointer-events-none select-none"
              aria-label={episodeLabel}
            >
              <CalendarIcon size={12} className="text-indigo-500 shrink-0" />
              <span className="text-[11px] font-semibold text-indigo-800 whitespace-nowrap">
                {episodeLabel}
              </span>
            </div>
          </div>
        }
      >
        <div className="p-3">
          {/* Classic flat calendar shell — no drop shadow */}
          <div className="border border-indigo-200 rounded-lg overflow-hidden bg-white">
            {/* Single toolbar row: nav | episode | views */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 bg-white border-b border-indigo-100">
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={goPrev}
                  className="inline-flex items-center justify-center h-7 w-7 rounded border border-indigo-200 bg-white text-indigo-500 hover:text-white hover:bg-indigo-500 hover:border-indigo-500 transition-colors"
                  title={
                    viewMode === "day"
                      ? "Previous day"
                      : viewMode === "week"
                        ? "Previous week"
                        : "Previous month"
                  }
                >
                  <ChevronLeftIcon size={14} />
                </button>
                <h4 className="text-sm font-semibold text-indigo-900 whitespace-nowrap px-1 min-w-[11rem] text-center">
                  {periodLabel}
                </h4>
                <button
                  type="button"
                  onClick={goNext}
                  className="inline-flex items-center justify-center h-7 w-7 rounded border border-indigo-200 bg-white text-indigo-500 hover:text-white hover:bg-indigo-500 hover:border-indigo-500 transition-colors"
                  title={
                    viewMode === "day"
                      ? "Next day"
                      : viewMode === "week"
                        ? "Next week"
                        : "Next month"
                  }
                >
                  <ChevronRightIcon size={14} />
                </button>
              </div>

              {episodeSubtitle ? (
                <p className="flex-1 min-w-0 text-[11px] text-indigo-500 text-center truncate px-1">
                  {episodeSubtitle}
                </p>
              ) : (
                <span className="flex-1" />
              )}

              {/* Segmented view control — joined, flat, no shadow */}
              <div className="inline-flex items-stretch shrink-0 ml-auto rounded border border-indigo-200 overflow-hidden bg-white">
                {["Month", "Week", "Day"].map((v, i) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setViewMode(v.toLowerCase())}
                    className={`h-8 px-3.5 text-xs font-bold transition-colors ${
                      i > 0 ? "border-l border-indigo-200" : ""
                    } ${
                      viewMode === v.toLowerCase()
                        ? "bg-indigo-500 text-white"
                        : "bg-white text-indigo-600 hover:bg-indigo-50"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {viewMode === "day" ? (
              <div className="grid grid-cols-1">
                {cells.map((cell, idx) =>
                  renderDayCell(cell, idx, { tall: true, showWeekday: true }),
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-7 bg-indigo-50/50 border-b border-indigo-100">
                  {DAYS.map((d) => (
                    <div
                      key={d}
                      className="px-1 py-2 text-center text-[11px] font-semibold text-indigo-600 uppercase tracking-wider"
                    >
                      {d}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {cells.map((cell, idx) =>
                    renderDayCell(cell, idx, {
                      tall: viewMode === "week",
                    }),
                  )}
                </div>
              </>
            )}
          </div>

          {/* Legend — Visit Type colors + coverage states */}
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-3">
            {VISIT_TYPES.map((t) => (
              <div key={t.value} className="flex items-center gap-1">
                <span
                  className="w-3 h-3 rounded-sm shrink-0"
                  style={{ backgroundColor: t.color }}
                />
                <span className="text-[10px] text-slate-500 font-medium">
                  {t.label}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-1">
              <span
                className="w-3 h-3 rounded-sm shrink-0 bg-slate-300"
                style={{ boxShadow: FREQUENCY_MISSING_SWATCH_SHADOW }}
              />
              <span
                className="text-[10px] font-semibold"
                style={{ color: FREQUENCY_MISSING_HEX }}
              >
                {FREQUENCY_MISSING_LABEL.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span
                className="w-3 h-3 rounded-sm shrink-0 bg-slate-300"
                style={{
                  boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${UNAUTHORIZED_HEX}`,
                }}
              />
              <span
                className="text-[10px] font-semibold"
                style={{ color: UNAUTHORIZED_HEX }}
              >
                {UNAUTHORIZED_LABEL.toUpperCase()}
              </span>
            </div>
            <div
              className="flex items-center gap-1"
              title="Same visit: unauthorized and frequency missing"
            >
              <span
                className="relative w-3 h-3 rounded-sm shrink-0 overflow-hidden"
                aria-hidden
              >
                <span
                  className="absolute inset-y-0 left-0 w-1/2"
                  style={{ backgroundColor: FREQUENCY_MISSING_HEX }}
                />
                <span
                  className="absolute inset-y-0 right-0 w-1/2"
                  style={{ backgroundColor: UNAUTHORIZED_HEX }}
                />
              </span>
              <span className="text-[10px] font-semibold">
                <span style={{ color: UNAUTHORIZED_HEX }}>
                  {UNAUTHORIZED_LABEL.toUpperCase()}
                </span>
                <span className="text-slate-400"> + </span>
                <span style={{ color: FREQUENCY_MISSING_HEX }}>
                  {FREQUENCY_MISSING_LABEL.toUpperCase()}
                </span>
              </span>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-6">
              <span className="w-5 h-5 border-2 border-indigo-300 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
      </CollapsibleSection>

      <ScheduleVisitModal
        open={modalOpen}
        mode={modalMode}
        visit={editingVisit}
        defaultDate={defaultDate}
        assignedStaff={assignedStaff}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        saving={saving}
        deleting={deleting}
        onDelete={
          canDelete &&
          modalMode === "edit" &&
          editingVisit?.id &&
          editingVisit?.canDelete !== false
            ? () => handleDeleteVisit(editingVisit.id)
            : undefined
        }
      />

      <ViewFrequencyModal
        open={frequencyOpen}
        onClose={() => setFrequencyOpen(false)}
        patientId={patientId}
        patient={patient}
        episodeMeta={episodeMeta}
        assignedStaff={assignedStaff}
        frequencies={frequencies}
        canEdit={canEdit}
        onChanged={() => {
          loadVisits();
          loadFrequencies();
        }}
      />
    </>
  );
};

export default PatientScheduleSection;
